import React from 'react'
import ReactDOM from 'react-dom'
import AnnotationBubbles from './AnnotationBubbles'
import ResolvedAnnotationsHandler from '../../components/ResolvedAnnotationsHandler'

/**
 * This class listens for clicks on proofs and pops over an annotation window
 * when a click is detected. In order to minimize the need to write dynamic
 * js/ajax requests perform page updates, this class uses the ManualForm class
 * to append a <form> tag to the page, add params to it, set the url and HTTP
 * method, and then do a full submit request/response cycle.
 *
 * Many of the available methods on this class are called back to from within
 * our React components. This is basically to keep things simple. Essentially:
 *
 * Event (click, etc) ->
 * component handler ->
 * Annotations.init().methodCall(args)
 */
export default class Annotations {
  /**
   * Sets up a new Annotations object to listen for clicks and activate
   * annotation popover.
   * @constructor
   */
  constructor(currPage) {
    this.baseUrl = '/annotations/';
    this.pageNumber = currPage || null

    this.loadAnnotationData();
    this.renderPageAnnotations();
    this.listen();
    if (this.showAnnotationOnLoadId){
      this.showAnnotation(this.showAnnotationOnLoadId)
      // remove current_annotation from the params
      let pageNumber = this.pageNumber ? `?page=${this.pageNumber}` : "?"
      window.history.replaceState({ turbolinks: {} }, "", `${pageNumber}`)
    }

    this.saveOptionSelection = this.saveOptionSelection.bind(this);
  }

  addFormAnnotationAttrs(form, annotation) {
    form.setRootParam('annotation')
    form.addInput('annotatable_type', this.jobType);
    form.addInput('annotatable_id', this.jobId);
    form.addInput('percent_x', annotation.percent_x);
    form.addInput('percent_y', annotation.percent_y);
    form.addInput('page', annotation.page);
  }

  closeCurrentAnnotation(cb) {
    if (this.currentAnnotation) {
      ReactDOM.unmountComponentAtNode(this.mountNode());
      this.currentAnnotation = false
      this.newAnnotation = null

      if (cb) {
        cb()
      }
    }
  }

  listen() {
    N2.handle("click", ".annotated-asset .js-annotation-boundry", (e) => {
      if (this.currentAnnotation) {
        this.closeCurrentAnnotation()
      } else {
        if (!this.readOnly && !this.disableNewAnnotations) {
          this.markNewAnnotation(e);
        }
      }
    })
  }

  loadAnnotationData() {
    let data = $('.annotations-data');
    this.annotations = data.data('annotations');
    this.annotationTypes = data.data('types');
    this.roleType = data.data('role-type');
    this.readOnly = data.data('readOnly')
    this.disableNewAnnotations = data.data('disableNewAnnotations') || false;
    this.jobId = data.data('jobId')
    this.jobType = data.data('jobType')
    // only used for field user wizard
    this.jobStatus = data.data('jobStatus')
    this.showAnnotationOnLoadId = data.data('currentAnnotation')
    this.lateArticleOptions = data.data('lateArticleOptions')
    this.emojis = data.data('emojis')
  }

  markNewAnnotation(e) {
    let number = this.annotations.length + 1;
    let $el = $(e.currentTarget)
    let x = ( (e.offsetX - 15) / $el.width()) * 100
    let y = ( (e.offsetY - 15) / $el.height()) * 100

    this.syncCurrentPageFromUrl();

    this.newAnnotation = {
      id: null,
      percent_x: x,
      percent_y: y,
      page: this.pageNumber,
      messages: [],
      can_delete: true,
    }
    let annotation = this.currentAnnotation = this.newAnnotation;
    this.renderPopup(annotation, number, 'new')
  }

  mountNode() {
    if (this._mountNode == null) {
      this._mountNode = $(".annotated-asset .asset-annotation-target")[0];
    }
    return this._mountNode
  }

  popupComponent(annotation, number, id) {
    return null;
  }

  syncCurrentPageFromUrl() {
    let regex = /\page=(\d+)/;
    let matchData = window.location.search.match(regex);
    var queryStringPage;
    if (matchData) {
      queryStringPage = matchData[1];
    } else {
      queryStringPage = "1";
    }

    this.pageNumber = queryStringPage;
  }

  /**
   * Yield dynamic control to React components. From here, we show a React
   * Bootstrap overlay filled with the content necessary for making pub
   * annotations. Several of the components involved with this workflow end up
   * needing to call back to pub annotations which.
   *
   * @param {object} annotation - an Annotation object, either new or existing
   * @param {string} number - The number shown on the page of the annotation
   * @param {integer} id - Id of the annotation, or 'new' if new
   */
  renderPopup(annotation, number, id) {
    ReactDOM.render(this.popupComponent(annotation, number, id), this.mountNode());
  }

  renderPageAnnotations() {
    if (this.annotations == undefined) { return }
    let pageSpecificAnnotations = $.grep(this.annotations, (a) => { return a.page == this.pageNumber; });
    ReactDOM.render(<AnnotationBubbles klass={this} allAnnotations={this.annotations} annotations={pageSpecificAnnotations} />, $('.js-annotations')[0]);
  }

  resolveAnnotation(annotation) {
    let form = new N2Styles.manualForm(`${this.baseUrl}${annotation.id}`)
    const toggle = annotation.resolved == true ? false : true
    form.addInput('resolved', toggle)
    form.form.attr('method', 'patch');
    form.setRootParam('annotation');

    form.sync({
      success: this.resolvedSuccess,
      error: this.resolvedError,
      context: this
    });
  }

  saveOptionSelection(annotation, currentItem) {
    let selection = $("select#annotationSelect").val();

    if (currentItem != selection) {
      let form = new N2Styles.manualForm();
      form.form.attr('method', 'patch');
      form.setRootParam('annotation');
      form.setUrl(`/annotations/${annotation.id}`);

      form.addInput('messages_attributes[0][content]', selection);
      form.addInput('messages_attributes[0][action_taken]', "option_selected");

      form.sync({
        success: this.submitCommentSuccess,
        error: this.submitCommentError,
        context: this,
      });
    }
  }

  saveLateArticleAnnotation(annotation, opts){
    let form = new N2Styles.manualForm(this.baseUrl)
    form.form.attr('method', 'post');
    form.addInput('type', 'Annotation::AddArticle');
    form.addInput('article_job_id', opts.articleId)
    this.addFormAnnotationAttrs(form, annotation);

    form.sync({
      success: this.submitCommentSuccess,
      error: this.submitCommentError,
      context: this
    });
  }

  saveAddPhotoAnnotation(annotation, file = false){
    let form = new N2Styles.manualForm(this.baseUrl)
    form.form.attr('method', 'post');
    if (file){
      form.addInput('type', 'Annotation::AddFiles');
    } else {
      form.addInput('type', 'Annotation::AddPhoto');
    }
    this.addFormAnnotationAttrs(form, annotation);

    form.sync({
      success: (resp) => {
        // using window.location vs N2.navigate to avoid turbolinks
        window.location = `/annotations/${resp.id}/assets`
      },
      error: this.submitCommentError,
      context: this
    });
  }

  saveRemoveTypeAnnotation(annotation, opts) {
    let form = new N2Styles.manualForm(this.baseUrl)
    form.form.attr('method', 'post');
    form.addInput('type', opts.type);
    this.addFormAnnotationAttrs(form, annotation);

    form.sync({
      success: this.submitCommentSuccess,
      error: this.submitCommentError,
      context: this
    });
  }

  saveRemoveArticleAnnotation(annotation) {
    let form = new N2Styles.manualForm(this.baseUrl)

    form.addInput('annotation[type]', 'Annotation::RemoveArticle');
    this.addFormAnnotationAttrs(form, annotation);

    form.post();
  }

  saveRemovePagesAnnotation(annotation, pages) {
    let form = new N2Styles.manualForm(this.baseUrl)
    form.form.attr('method', 'post');
    form.addInput('type', 'Annotation::RemovePages');
    this.addFormAnnotationAttrs(form, annotation);
    form.addInput('pages[]', pages)

    form.sync({
      success: this.submitCommentSuccess,
      error: this.submitCommentError,
      context: this
    });
  }

  showAnnotation(id) {
    let found = null;
    let offset = -1;
    this.annotations.forEach((ann, idx) => {
      if (ann.id == id) {
        found = ann;
        offset = idx;
      }
    });
    let annotation = this.currentAnnotation = found
    let number = offset + 1;
    this.renderPopup(annotation, number, number)
  }

  submitComment(annotation, message, attachment = null) {
    if (message.length == 0 && attachment == null){
      new N2Styles.toast('You must type something in the message box.')
      return false
    }

    let form = new N2Styles.manualForm()
    let url = this.baseUrl;
    if (annotation.id) {
      url += `${annotation.id}`

      form.addInput('id', annotation.id);
      form.form.attr('method', 'patch');
    } else {
      form.form.attr('method', 'post');
    }
    form.setUrl(url)
    this.addFormAnnotationAttrs(form, annotation);
    form.addInput('type', annotation.type || "Annotation::Comment");

    if (attachment) {
      form.addInput('messages_attributes[0][asset_base64]', attachment);
    } else {
      form.addInput('messages_attributes[0][content]', message);
    }

    form.sync({
      success: this.submitCommentSuccess,
      error: this.submitCommentError,
      context: this
    })
  }

  deleteComment(message, force = false) {
    let form = new N2Styles.manualForm()
    let url;
    if (force) {
      url = this.baseUrl;
      url += `${this.currentAnnotation.id}`
    } else {
      url = '/annotation_messages/';
      url += `${message.id}`
    }
    form.setUrl(url)
    form.form.attr('method', 'delete');

    form.sync({
      success: this.deleteCommentSuccess,
      error: this.deleteCommentError,
      context: this
    })
  }

  updateAnnotationPosition(annotation, opts = {}) {
    let form = new N2Styles.manualForm()
    form.setRootParam('annotation')

    let url = this.baseUrl;
    url += `${annotation.id}`
    form.addInput('id', annotation.id);
    form.form.attr('method', 'patch');
    form.setUrl(url)

    form.addInput('page', annotation.page);
    form.addInput('percent_x', opts['x']);
    form.addInput('percent_y', opts['y']);

    form.sync({
      success: this.updatePositionSuccess,
      error: this.updatePositionError,
      context: this
    })
  }

  createEmptyAnnotation(annotation) {
    let form = new N2Styles.manualForm()

    form.setUrl(this.baseUrl);

    this.addFormAnnotationAttrs(form, annotation);

    form.addInput('annotation[type]', annotation.type);
    form.post()
  }

  // CALLBACKS
  resolvedSuccess(resp){
    const ann = this.grepAnnotationId(resp.id)
    this.annotations[this.annotations.indexOf(ann)] = resp
    this.renderPageAnnotations();
    this.renderPopup(resp, this.annotations.indexOf(resp) + 1, resp.id)
    new ResolvedAnnotationsHandler()
  }

  resolvedError(resp) {
    new N2Styles.toast('Unable to resolve the annotation at this time.')
  }

  submitCommentSuccess(resp) {
    let ann = this.grepAnnotationId(resp.id);
    if (ann) {
      ann.messages = resp.messages
    } else {
      this.annotations.push(resp)
      ann = this.annotations.slice(-1)[0]
      this.renderPageAnnotations();
    }
    if (this.annotations.length){
      $('.js-no-annotation-text').addClass('hidden')
    }
    this.currentAnnotation = ann
    this.renderPopup(ann, this.annotations.indexOf(ann) + 1, ann.id)
  }

  submitCommentError(req) {
    let msg = 'Unable to save your annotation at this time.'
    // TODO maybe use some custom internal code on json response
    if (req.responseJSON.errors.includes('Annotation location')){
      msg = 'Your annotation is too close to the edge and cannot be saved. Please try again.'
    } else if (req.responseJSON.errors.includes('role')) { // WETS-211
      msg = req.responseJSON.errors;
    }
    new N2Styles.toast(msg)
  }

  updatePositionSuccess(resp) {
    const ann = this.grepAnnotationId(resp.id);
    this.annotations[this.annotations.indexOf(ann)] = resp
    this.renderPageAnnotations();
  }

  updatePositionError(req) {
    const ann = req.responseJSON.annotation
    const $target = $(`[data-id='${ann.id}']`)
    const prevPosition = $target.data('draggabilly').startPosition
    $target.attr('style', `left: ${prevPosition.x}px; top: ${prevPosition.y}px`)
    new N2Styles.toast('Please try that move again. Thanks.')
  }

  deleteCommentError(req) {
    new N2Styles.toast('Unable to delete your comment at this time.')
  }

  deleteCommentSuccess(resp, status, req) {
    if (req.status == 204) {
      const ann = this.grepAnnotationId(this.currentAnnotation.id)
      this.annotations.splice([this.annotations.indexOf(ann)], 1)
      this.closeCurrentAnnotation();
      this.renderPageAnnotations();
    } else {
      const ann = this.grepAnnotationId(resp.annotation.id)
      ann.messages = resp.annotation.messages
      this.renderPopup(ann, this.annotations.indexOf(ann) + 1, ann.id)
    }
    if (this.annotations.length) {
      $('.js-no-annotation-text').addClass('hidden')
    } else {
      $('.js-no-annotation-text').removeClass('hidden')
    }
  }

  grepAnnotationId(annId) {
    return $.grep(this.annotations, (a) => { return a.id == annId; })[0];
  }
}
